/**
 * Turns a free-text place — a city name, a ZIP code, an address — into
 * coordinates the marketplace search endpoint can use.
 *
 * The marketplace ("Find A Space") only searches by latitude/longitude, so a
 * buyer asking "how many locations in Los Angeles" or "find me locations in
 * 34741" has to be geocoded first. The rest of the app does this with Google's
 * browser geocoder, loaded app-wide via `GeocoderProvider`; this wraps that same
 * geocoder behind a small, injectable function so a skill can call it without
 * knowing about Google, and so tests can substitute a stub.
 *
 * It is deliberately total: any failure — the library not loaded yet, no match,
 * a thrown error — resolves to `null` rather than rejecting, because a location
 * search that cannot geocode should degrade to an honest "I could not find that
 * place" rather than surface a Google error to the user.
 */

/** A resolved point, in the `{lat, lon}` shape the search endpoint expects. */
export interface GeoPoint {
  lat: number;
  lon: number;
}

/** Resolves a place description to coordinates, or `null` when it cannot. */
export type GeocodeFn = (term: string) => Promise<GeoPoint | null>;

/** The subset of a `google.maps.GeocoderRequest` this module builds. */
interface GeocodeRequest {
  address?: string;
  componentRestrictions?: { country?: string; postalCode?: string };
}

/** The slice of `google.maps.Geocoder` this module uses. Kept structural so a
 *  test can pass a plain object and so we never import the Google types here. */
interface GeocoderLike {
  geocode: (request: GeocodeRequest) => Promise<{
    results: Array<{
      types?: string[];
      geometry?: { location?: { lat: () => number; lng: () => number } };
    }>;
  }>;
}

/** A bare 5-digit US ZIP, with nothing else around it. */
const BARE_ZIP = /^\d{5}$/;

/**
 * Builds the geocoder request for a term.
 *
 * SecurSpace's marketplace is US-only, so every lookup is biased to the US —
 * this both disambiguates a bare city name and, crucially, fixes bare ZIPs: the
 * JS Geocoder returns ZERO_RESULTS for some 5-digit ZIPs typed alone (e.g.
 * "90210") that resolve fine once constrained to a US postal code. A ZIP is
 * therefore sent as a `postalCode` component rather than free-text `address`.
 */
const buildRequest = (address: string): GeocodeRequest =>
  BARE_ZIP.test(address)
    ? { componentRestrictions: { country: 'US', postalCode: address } }
    : { address, componentRestrictions: { country: 'US' } };

/** True when `value` looks enough like a geocoder to call. */
const isGeocoder = (value: unknown): value is GeocoderLike =>
  typeof (value as { geocode?: unknown } | null)?.geocode === 'function';

/** A geocode that always declines, used when no geocoder is available. */
export const noGeocode: GeocodeFn = async () => null;

/**
 * Obtains a usable geocoder at *call* time from the global `google.maps`.
 *
 * `GeocoderContext` populates its geocoder from a one-shot effect that awaits
 * `importLibrary('geocoding')`; if a user asks before that effect has resolved,
 * the context still holds an empty object and the assistant would wrongly report
 * "I could not find that place" for even a valid ZIP. `index.html` loads the
 * Maps bootstrap synchronously before React mounts, so the global exists app-wide
 * regardless of that effect — this is the resilient fallback that uses it. Kept
 * total: any absence or failure resolves to `null`.
 */
const geocoderFromGlobal = async (): Promise<GeocoderLike | null> => {
  const maps = (globalThis as { google?: { maps?: unknown } }).google?.maps as
    | {
        Geocoder?: new () => unknown;
        importLibrary?: (name: string) => Promise<{ Geocoder?: new () => unknown }>;
      }
    | undefined;
  if (!maps) return null;

  try {
    if (typeof maps.Geocoder === 'function') {
      const geocoder = new maps.Geocoder();
      return isGeocoder(geocoder) ? geocoder : null;
    }
    if (typeof maps.importLibrary === 'function') {
      const { Geocoder } = await maps.importLibrary('geocoding');
      if (typeof Geocoder !== 'function') return null;
      const geocoder = new Geocoder();
      return isGeocoder(geocoder) ? geocoder : null;
    }
  } catch {
    return null;
  }
  return null;
};

/** Runs a single geocode request through a ready geocoder, totally. */
const runGeocode = async (
  geocoder: GeocoderLike,
  address: string,
): Promise<GeoPoint | null> => {
  try {
    const { results } = await geocoder.geocode(buildRequest(address));
    const top = results?.[0];
    const location = top?.geometry?.location;
    if (!location) return null;

    // The US country bias makes the geocoder answer an unrecognised place with
    // the country centroid rather than nothing; treat a country-level match as
    // "not found" so a nonsense term degrades honestly instead of searching the
    // middle of the map.
    if (top?.types?.includes('country')) return null;

    const lat = location.lat();
    const lon = location.lng();
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

    return { lat, lon };
  } catch {
    return null;
  }
};

/**
 * Builds a {@link GeocodeFn} over a Google geocoder instance.
 *
 * `geocoder` is typed loosely because it arrives from `GeocoderContext`, whose
 * value is `any` until the Google library has loaded — before then it is an
 * empty object. Rather than binding "is there a geocoder" once at build time,
 * each call tries the injected geocoder first and, if it is not usable **or
 * returns nothing**, falls back to one obtained from the global `google.maps`
 * ({@link geocoderFromGlobal}). `GeocoderContext`'s instance is not reliably
 * usable for forward text geocoding in this app, so the global — which
 * `index.html` loads synchronously before React mounts — is the dependable
 * source, and a search still geocodes instead of falsely reporting the place as
 * unknown.
 */
export const createGeocode = (geocoder: unknown): GeocodeFn =>
  async (term: string): Promise<GeoPoint | null> => {
    const address = term.trim();
    if (!address) return null;

    // Try the injected context geocoder first when it looks ready.
    if (isGeocoder(geocoder)) {
      const point = await runGeocode(geocoder, address);
      if (point) return point;
    }

    // Fall back to a geocoder built from the global `google.maps`. This covers
    // both the window before `GeocoderContext` resolves and the case where its
    // instance never became usable, so a valid place still geocodes instead of
    // being falsely reported as unknown.
    const global = await geocoderFromGlobal();
    if (global) return runGeocode(global, address);

    return null;
  };
